// Polyfill Buffer and process for browser environment (required by Privy SDK)
import { Buffer } from 'buffer';
import process from 'process';

window.Buffer = Buffer;
global.Buffer = Buffer;
window.process = process;
global.process = process;

// The `process` browser polyfill reports a Node version, which makes the
// StableTrust SDK take its Node WASM/fs path in the browser. Force the
// browser branch so WASM loads via the bundler instead of `fs.readFileSync`.
if (process.versions && process.versions.node) {
  delete process.versions.node;
}

// Suppress known Rainbow Wallet extension errors
// Rainbow Wallet's inpage script sometimes calls chrome.runtime.sendMessage() incorrectly
// This is a known issue with the Rainbow Wallet extension, not our code
if (typeof window !== 'undefined') {
    const originalError = window.onerror;
    window.onerror = function(message, source, lineno, colno, error) {
        // Suppress Rainbow Wallet's chrome.runtime.sendMessage error
        const messageStr = message ? String(message) : '';
        const sourceStr = source ? String(source) : '';
        const errorStr = error ? (error.message || String(error)) : '';
        const combinedStr = `${messageStr} ${sourceStr} ${errorStr}`;
        
        if (combinedStr.includes('chrome.runtime.sendMessage') || 
            combinedStr.includes('Extension ID') ||
            combinedStr.includes('runtime.sendMessage') ||
            combinedStr.includes('opfgelmcmbiajamepnmloijbpoleiama') ||
            combinedStr.includes('Error in invocation of runtime.sendMessage')) {
            // This is a known Rainbow Wallet extension issue - suppress it
            return true; // Suppress the error
        }
        
        // Call original error handler for other errors
        if (originalError) {
            return originalError.call(this, message, source, lineno, colno, error);
        }
        return false;
    };
    
    // Also catch unhandled promise rejections from Rainbow Wallet
    window.addEventListener('unhandledrejection', function(event) {
        const reason = event.reason;
        if (reason) {
            const message = reason?.message || String(reason);
            const stack = reason?.stack || '';
            const combinedStr = `${message} ${stack}`;
            if (combinedStr.includes('chrome.runtime.sendMessage') || 
                combinedStr.includes('Extension ID') ||
                combinedStr.includes('runtime.sendMessage') ||
                combinedStr.includes('opfgelmcmbiajamepnmloijbpoleiama') ||
                combinedStr.includes('Error in invocation of runtime.sendMessage')) {
                // Suppress Rainbow Wallet extension errors
                event.preventDefault(); // Prevent the error from being logged
                event.stopPropagation(); // Stop propagation
            }
        }
    });
    
    // Override console.error to suppress Rainbow Wallet errors in React's error overlay
    const originalConsoleError = console.error;
    console.error = function(...args) {
        const errorString = args.map(arg => {
            if (arg instanceof Error) {
                return `${arg.message} ${arg.stack || ''}`;
            }
            return String(arg);
        }).join(' ');
        if (errorString.includes('chrome.runtime.sendMessage') ||
            errorString.includes('Extension ID') ||
            errorString.includes('runtime.sendMessage') ||
            errorString.includes('opfgelmcmbiajamepnmloijbpoleiama') ||
            errorString.includes('Error in invocation of runtime.sendMessage')) {
            // Suppress Rainbow Wallet errors from console.error
            return;
        }
        // Call original console.error for other errors
        originalConsoleError.apply(console, args);
    };
    
    // Suppress React's error overlay for Rainbow Wallet errors
    // React's error overlay listens to window errors, so we need to catch them early
    // The error handlers above should catch most cases, but React's overlay might still show them
    // If the overlay still appears, it's a known issue with the Rainbow Wallet extension
    // and doesn't affect the functionality of the app
}

import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { PrivyProvider } from '@privy-io/react-auth';
import { WagmiProvider, createConfig } from '@privy-io/wagmi';
import { http } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';
// "Editorial hairline" restyle — source of truth is handoff/reference.html
// at the repo root. Drop-in stylesheet targeting the app's existing class
// names directly (no JSX changes needed), copied in unmodified from
// handoff/stabletrust-editorial.css. Imported last (after App, so after
// App.css) per that file's own instruction, so it wins the cascade — it
// also uses !important throughout as a safety net regardless of order.
// Fonts (Instrument Serif, JetBrains Mono, Space Grotesk) are loaded from
// Google Fonts in public/index.html.
import './stabletrust-editorial.css';

// Import chain configurations to get chain IDs
import { CHAINS } from './config/chains';
// Import wagmi chain definitions
import { 
  arbitrumMainnet,
  supportedWagmiChains 
} from './config/wagmiChains';

// Get all active chain IDs for Privy configuration
const getActiveChainIds = () => {
  return Object.values(CHAINS)
    .filter(chain => chain.active)
    .map(chain => chain.chainIdDecimal)
    .filter(id => id > 0); // Filter out chains with 0 chainId (like Solana, Noble)
};

// Create QueryClient for React Query
const queryClient = new QueryClient();

// Create wagmi config with all active chains using Privy's createConfig
// Dynamically build transports object for all supported chains
const buildTransports = () => {
  const transports = {};
  supportedWagmiChains.forEach(chain => {
    if (chain && chain.rpcUrls?.default?.http?.[0]) {
      transports[chain.id] = http(chain.rpcUrls.default.http[0]);
    }
  });
  return transports;
};

const wagmiConfig = createConfig({
  chains: supportedWagmiChains,
  transports: buildTransports(),
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <ErrorBoundary>
    <PrivyProvider
      appId={process.env.REACT_APP_PRIVY_APP_ID}
      clientId={process.env.REACT_APP_PRIVY_CLIENT_ID || undefined}
      config={{
        // Arbitrum One is the product default (mainnet landing). Must also be
        // in supportedChains or Privy rejects switchChain(42161).
        defaultChain: arbitrumMainnet,
        supportedChains: supportedWagmiChains,
        // Auto-provision an embedded EOA on login for users who don't have one yet
        embeddedWallets: { 
          ethereum: { 
            createOnLogin: 'users-without-wallets',
            // Include all active chain IDs (Arbitrum One, Sepolia, Base, Arc, Stable, Tempo, etc.)
            chains: getActiveChainIds()
          } 
        },
        // Shown in the Privy modal: email, Google, and external wallets
        // ("Continue with a wallet" -> the walletList below).
        // Each of these must ALSO be enabled in the Privy dashboard for this
        // app id — the dashboard is the source of truth and this array can
        // only narrow it, never add a method the dashboard has switched off.
        loginMethods: ['email', 'passkey', 'google', 'wallet'],
        appearance: {
          // EVM-only app — don't surface Solana wallets.
          walletChainType: 'ethereum-only',
          // Keep email/social on top, wallets behind the "Continue with a
          // wallet" row, matching the reference layout.
          showWalletLoginFirst: false,
          // Deliberately a short allow-list of plain EOA extension wallets.
          //
          // A user's ElGamal keypair is derived from the raw bytes of one
          // EIP-712 signature (see deriveElGamalKeypair in
          // services/stabletrustService.js), so a wallet MUST return a
          // byte-identical signature every time or the derived key changes
          // between sessions and their confidential balance stops decrypting —
          // silently, because the local key cache hides it until the cache is
          // cleared or they open the app on another device.
          //
          // That rules out, and is why you will not find here:
          //   - passkey / smart-contract accounts ('base_account', 'safe',
          //     and 'coinbase_wallet', whose connector can hand back the
          //     passkey smart wallet): WebAuthn and EIP-1271 signatures are
          //     not byte-stable.
          //   - MPC / threshold-signature wallets ('binance', 'binanceus'):
          //     the signing nonce is generated jointly at random, so two
          //     signatures over the same message differ.
          //   - 'wallet_connect': a protocol, not a wallet — it can pair a
          //     smart account (Safe, Argent) that hits the same problem.
          //   - 'detected_ethereum_wallets': would surface whatever the user
          //     has installed, including Phantom, which the app blocks
          //     outright (isPhantomProvider in App.js) after login.
          //   - 'uniswap': rejects connection with "network is not supported"
          //     against our Privy chain set (Arbitrum + custom testnets like
          //     Arc/Stable). Prefer omitting a wallet over showing one that
          //     errors on connect.
          //   - 'rainbow': cannot cover our full active chain set. Mobile has
          //     no testnets and no custom networks (Privy warns it rejects
          //     testnet defaultChain). Extension built-in testnets still omit
          //     Arbitrum Sepolia / Base Sepolia, and wallet_addEthereumChain
          //     for custom nets (Arc 5042002, Stable 2201) is unreliable.
          //
          // Active EVM chains wallets below must handle (via native support
          // or wallet_addEthereumChain): Arbitrum One, Ethereum, Base, BNB,
          // Robinhood mainnets; Arbitrum/Ethereum/Base/BNB Sepolia-or-testnet;
          // Arc testnet; Stable testnet.
          // OKX and Crypto.com Onchain document custom EVM / testnet network
          // add; MetaMask is the reference for that path.
          //
          // Everything below is a single-key EOA browser extension signing
          // with standard secp256k1 (RFC-6979 deterministic nonce).
          walletList: [
            'metamask',
            'okx_wallet',
            'cryptocom',
          ],
        }
      }}
    >
      <QueryClientProvider client={queryClient}>
        <WagmiProvider config={wagmiConfig}>
          <App />
        </WagmiProvider>
      </QueryClientProvider>
    </PrivyProvider>
  </ErrorBoundary>
);
